import type { JSX } from 'react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ClassStudentsSection } from '@/features/classes/class-students-section';
import { ClassTeachersSection } from '@/features/classes/class-teachers-section';
import { DeactivateClassButton } from '@/features/classes/deactivate-class-button';
import { EditClassDialog } from '@/features/classes/edit-class-dialog';
import { useClass } from '@/features/classes/hooks';
import { useClassProgress } from '@/features/memorization/hooks';
import { ProgressStatsGrid } from '@/features/memorization/progress-stats-grid';
import { useHasCapability } from '@/lib/capabilities';

/**
 * The class workspace (Phase G-6): class information, teacher assignment,
 * student enrollment, and aggregate Progress - one surface for every role
 * that can reach it, with visible controls capability-driven rather than
 * role-branched (matching Student Detail's established composition).
 */
export function ClassDetailPage(): JSX.Element {
  const { classId } = useParams<{ classId: string }>();
  const { data: classView, isLoading, isError } = useClass(classId ?? '');
  const [editOpen, setEditOpen] = useState(false);
  const canManage = useHasCapability('classes:manage');
  const canViewProgress = useHasCapability('memorization_targets:manage');

  if (classId === undefined) {
    return <p className="p-4 text-sm text-destructive">Kelas tidak ditemukan.</p>;
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
      <h1 className="sr-only">Detail Kelas</h1>
      {isLoading && <Skeleton className="h-32 w-full" />}
      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Gagal memuat data kelas.</AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && classView !== undefined && (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="min-w-0 truncate text-base">{classView.name}</CardTitle>
              {canManage && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditOpen(true);
                  }}
                >
                  Ubah Data
                </Button>
              )}
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="capitalize">
                  {classView.status.toLowerCase()}
                </Badge>
                {classView.academicYear !== null && (
                  <span className="text-sm text-muted-foreground">{classView.academicYear}</span>
                )}
              </div>
              <Link
                to={`/learning-spaces/${classView.learningSpaceId}`}
                className="text-sm text-muted-foreground hover:underline"
              >
                Lihat ruang belajar
              </Link>
              {canManage && (
                <div className="mt-2">
                  <DeactivateClassButton classId={classView.id} />
                </div>
              )}
            </CardContent>
          </Card>

          <ClassTeachersSection classId={classView.id} />

          <ClassStudentsSection classId={classView.id} />

          {canViewProgress && <ClassProgressCard classId={classView.id} />}

          <EditClassDialog classView={classView} open={editOpen} onOpenChange={setEditOpen} />
        </>
      )}
    </div>
  );
}

function ClassProgressCard({ classId }: { readonly classId: string }): JSX.Element {
  const { data, isLoading, isError } = useClassProgress(classId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Progress Kelas</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLoading && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription>Gagal memuat progres kelas.</AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && data !== undefined && (
          <>
            <ProgressStatsGrid data={data} />
            <span className="text-xs text-muted-foreground">
              Terakhir direkonsiliasi:{' '}
              {data.lastReconciledAt !== null
                ? new Date(data.lastReconciledAt).toLocaleString('id-ID')
                : 'Belum pernah'}
            </span>
          </>
        )}
      </CardContent>
    </Card>
  );
}
